import { debounce } from "lodash";
import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { actions, Form } from "react-redux-form";
import { useLocation, useParams } from "react-router";
import Alert from "react-s-alert";
import { bindActionCreators } from "redux";
import * as NavigationActions from "../../../actions/navigation";
import * as TrialGroupActions from "../../../actions/trialGroup";
import * as TrialGroupContractActions from "../../../actions/trialGroupContract";
import { NotificationAlert } from "../../../components/common/index";
import {
    DatePickerFormInputFabric,
    TextFormInput,
} from "../../../components/form/index";
import { RestrictedLayout } from "../../../components/layouts/index";
import {
    FontAwesomeIcon,
    FontAwesomeIcons,
} from "../../../constants/fontAwesomeIcons";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import { isUrl } from "../../../functions/string";
import { renderRegistrationFormSaveBlock } from "../../../helpers/alert";
import * as AuthenticationHelper from "../../../helpers/authentication";
import {
    GroupTrialContractCreateBreadcrumbs,
    GroupTrialContractDetailsBreadcrumbs,
    TrialGroupContractCreateBreadcrumbs,
    TrialGroupContractDetailsBreadcrumbs,
} from "../../../helpers/breadcrumbHelper";
import {
    RegistrationComponent,
    renderFormControls,
} from "../../../helpers/formHelper";
import {
    GroupTrialContractSubMenu,
    TrialGroupContractSubMenu,
} from "../../../helpers/subMenuHelper";
import useNavigate from "../../../hooks/useNavigate";
import { RegistrationFormComponent } from "../../../interfaces/forms/IRegistrationComponent";
import { ITmdState } from "../../../reducers/index";
import { IRegistrationFormState } from "../../../reducers/reactReduxForms/index";
import { ITrialGroupContractReduxFormState } from "../../../reducers/reactReduxForms/trialGroupContract";

import "./TrialGroupContractPage.scss";

interface TrialGroupContractPageProps {
    form: Dtos.TrialGroupContract & IRegistrationFormState;
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    reduxFormState: ITrialGroupContractReduxFormState;
    trialGroupContract: Dtos.TrialGroupContract;
    lookups: Dtos.Lookup[];
    loadingTrialGroupContract: boolean;
    loadTrialGroupContractSuccess: boolean;
    loadTrialGroupContractFailure: boolean;
    loadTrialGroupContractErrorCode: string;
    savingTrialGroupContract: boolean;
    saveTrialGroupContractSuccess: boolean;
    saveTrialGroupContractFailure: boolean;
    creatingTrialGroupContract: boolean;
    createTrialGroupContractSuccess: boolean;
    createTrialGroupContractFailure: boolean;
    validatingTrialGroupContract: boolean;
    validationFailures: Dtos.ResponseError[];
    user: Dtos.User;
    permissions?: Dtos.PersonnelTmdPermissions[];
    trialGroup: Dtos.TrialGroup;
    loadingTrialGroup: boolean;
    loadTrialGroupSuccess: boolean;
    loadTrialGroupFailure: boolean;
    loadTrialGroupErrorCode: string;
    clearTrialGroupContract: TrialGroupContractActions.ITrialGroupContractClearActionCreator;
    loadTrialGroupContract: TrialGroupContractActions.ITrialGroupContractLoadByIdActionCreator;
    saveTrialGroupContract: TrialGroupContractActions.ITrialGroupContractSaveActionCreator;
    createTrialGroupContract: TrialGroupContractActions.ITrialGroupContractCreateActionCreator;
    validateTrialGroupContract: TrialGroupContractActions.ITrialGroupContractValidateActionCreator;
    loadForm: typeof actions.load;
    changeForm: typeof actions.change;
    resetForm: typeof actions.reset;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
    loadTrialGroup: TrialGroupActions.ITrialGroupLoadByIdActionCreator;
    clearTrialGroup: TrialGroupActions.ITrialGroupClearActionCreator;
}

const reduxFormName: string = "reduxForms.trialGroupContract";

const TrialGroupContractPage: FC<TrialGroupContractPageProps> = ({
    form,
    formState,
    formProperties,
    trialGroupContract,
    lookups,
    loadingTrialGroupContract,
    loadTrialGroupContractFailure,
    loadTrialGroupContractErrorCode,
    savingTrialGroupContract,
    saveTrialGroupContractSuccess,
    saveTrialGroupContractFailure,
    creatingTrialGroupContract,
    validatingTrialGroupContract,
    validationFailures,
    permissions,
    trialGroup,
    loadingTrialGroup,
    loadTrialGroupFailure,
    loadTrialGroupErrorCode,
    clearTrialGroupContract,
    loadTrialGroupContract,
    saveTrialGroupContract,
    createTrialGroupContract,
    validateTrialGroupContract,
    loadForm,
    changeForm,
    resetForm,
    setBreadcrumbs,
    setDrawerItems,
    loadTrialGroup,
    clearTrialGroup
}) => {
    //
    // -- Params --
    const { trialId } = useParams<{ trialId: string }>();
    const { groupId } = useParams<{ groupId: string }>();
    const { trialGroupId } = useParams<{ trialGroupId: string }>();
    const { trialGroupContractId } = useParams<{trialGroupContractId: string;}>();
    const trialIdNumber = parseInt(trialId, 10);
    const groupIdNumber = parseInt(groupId, 10);
    const trialGroupIdNumber = parseInt(trialGroupId, 10);
    const trialGroupContractIdNumber = parseInt(trialGroupContractId, 10);

    //
    // -- States --
    const [saveAndReturn, setSaveAndReturn] = useState<boolean>(false);
    const [formInitalised, setFormInitalised] = useState<boolean>(false);
    const [hasBeenSubmitted, setHasBeenSubmitted] = useState<boolean>(false);

    //
    // -- Hooks --
    const navigate = useNavigate();
    const location = useLocation();

    const loadReduxForm = () => {
        let extraData: Dtos.TrialGroupContract = {} as Dtos.TrialGroupContract;

        loadForm(
            reduxFormName,
            Object.assign({ ...trialGroupContract }, extraData)
        );
    };


    // Load Trial Group & Contract
    useEffect(() => {
        const baseRoute = location?.pathname?.split("/")[1];

        if (
            trialIdNumber &&
            groupIdNumber &&
            trialGroupIdNumber &&
            trialGroupContractIdNumber &&
            !trialGroupContract &&
            !loadingTrialGroupContract
        ) {
            const breadcrumbSetter =
                baseRoute === "trials"
                    ? TrialGroupContractDetailsBreadcrumbs
                    : GroupTrialContractDetailsBreadcrumbs;

            setBreadcrumbs(
                breadcrumbSetter(
                    trialIdNumber,
                    groupIdNumber,
                    trialGroupIdNumber,
                    trialGroupContractIdNumber
                )
            );

            loadTrialGroupContract(trialGroupContractIdNumber);
        } else if (
            trialIdNumber &&
            groupIdNumber &&
            trialGroupIdNumber &&
            !trialGroupContractIdNumber &&
            !trialGroupContract &&
            !creatingTrialGroupContract
        ) {
            const breadcrumbSetter =
                baseRoute === "trials"
                    ? TrialGroupContractCreateBreadcrumbs
                    : GroupTrialContractCreateBreadcrumbs;

            setBreadcrumbs(
                breadcrumbSetter(
                    trialIdNumber,
                    groupIdNumber,
                    trialGroupIdNumber
                )
            );
            setDrawerItems([]);
            createTrialGroupContract(trialGroupIdNumber);
        } else if (trialGroupContract && !formInitalised) {
            loadReduxForm();
            setFormInitalised(true);
        }

        if (trialGroupIdNumber && !trialGroup && !loadingTrialGroup) {
            loadTrialGroup(trialGroupIdNumber);
        }
    }, [
        location,
        trialIdNumber,
        groupIdNumber,
        trialGroupIdNumber,
        trialGroupContractIdNumber,
        trialGroupContract,
        formInitalised,
        setDrawerItems,
        setBreadcrumbs,
        loadTrialGroupContract,
        trialGroup,
        loadTrialGroup,
        setFormInitalised,
        TrialGroupContractDetailsBreadcrumbs,
        GroupTrialContractDetailsBreadcrumbs,
        TrialGroupContractCreateBreadcrumbs,
        GroupTrialContractCreateBreadcrumbs,
        createTrialGroupContract,
        loadReduxForm,
        loadingTrialGroupContract,
        creatingTrialGroupContract,
        loadingTrialGroup,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadTrialGroupContractFailure) {
            navigate(`/error/${loadTrialGroupContractErrorCode}`);
        }
        if (loadTrialGroupFailure) {
            navigate(`/error/${loadTrialGroupErrorCode}`);
        }
    }, [
        loadTrialGroupContractFailure,
        loadTrialGroupFailure,
        loadTrialGroupContractErrorCode,
        loadTrialGroupErrorCode,
        navigate,
    ]);

    // Update breadcrumbs
    useEffect(() => {
        const baseRoute = location?.pathname?.split("/")[1];

        if (
            trialIdNumber &&
            groupIdNumber &&
            trialGroupIdNumber &&
            trialGroupContractIdNumber &&
            trialGroupContract &&
            trialGroup
        ) {
            const breadcrumbSetter =
                baseRoute === "trials"
                    ? TrialGroupContractDetailsBreadcrumbs
                    : GroupTrialContractDetailsBreadcrumbs;

            const drawerItemSetter =
                baseRoute === "trials"
                    ? TrialGroupContractSubMenu
                    : GroupTrialContractSubMenu;

            setDrawerItems(drawerItemSetter(trialGroupContract, permissions));
            setBreadcrumbs(
                breadcrumbSetter(
                    trialIdNumber,
                    groupIdNumber,
                    trialGroupIdNumber,
                    trialGroupContractIdNumber,
                    trialGroup?.trial?.displayName,
                    trialGroup?.group?.name,
                    trialGroupContract?.name
                )
            );
        } else if (
            trialIdNumber &&
            trialGroupIdNumber &&
            !trialGroupContractIdNumber &&
            trialGroup
        ) {
            const breadcrumbSetter =
                baseRoute === "trials"
                    ? TrialGroupContractCreateBreadcrumbs
                    : GroupTrialContractCreateBreadcrumbs;

            setBreadcrumbs(
                breadcrumbSetter(
                    trialIdNumber,
                    groupIdNumber,
                    trialGroupIdNumber,
                    trialGroup?.trial?.displayName,
                    trialGroup?.group?.name
                )
            );
        }
    }, [
        location,
        trialIdNumber,
        groupIdNumber,
        trialGroupIdNumber,
        trialGroupContractIdNumber,
        trialGroupContract,
        trialGroup,
        permissions,
        setDrawerItems,
        setBreadcrumbs,
        TrialGroupContractDetailsBreadcrumbs,
        GroupTrialContractDetailsBreadcrumbs,
        TrialGroupContractCreateBreadcrumbs,
        GroupTrialContractCreateBreadcrumbs,
        TrialGroupContractSubMenu,
        GroupTrialContractSubMenu,
    ]);

    // Reload trial group contract after saving
    useEffect(() => {
        if (!savingTrialGroupContract && saveTrialGroupContractSuccess) {
            Alert.success(
                <NotificationAlert
                    alertContent={<span>Trial Group Contract saved successfully</span>}
                    icon={FontAwesomeIcons.Solid.CHECK}
                />
            );

            const baseRoute = location?.pathname?.split("/")[1];

            if (saveAndReturn) {
                const url =
                    baseRoute === "trials"
                        ? `/trials/${trialIdNumber}/groups/${groupIdNumber}/trialgroup/${trialGroupIdNumber}/contracts`
                        : `/groups/${groupIdNumber}/trials/${trialIdNumber}/trialgroup/${trialGroupIdNumber}/contracts`;
                navigate(url);
            } else if (!trialGroupContractIdNumber) {
                const url =
                    baseRoute === "trials"
                        ? `/trials/${trialIdNumber}/groups/${groupIdNumber}/trialgroup/${trialGroupIdNumber}/contracts/${trialGroupContract?.id}/details`
                        : `/groups/${groupIdNumber}/trials/${trialIdNumber}/trialgroup/${trialGroupIdNumber}/contracts/${trialGroupContract?.id}/details`;
                navigate(url);
                clearTrialGroupContract();
            } else {
                loadTrialGroupContract(trialGroupContractIdNumber);
            }
        }
    }, [
        location,
        trialIdNumber,
        groupIdNumber,
        trialGroupIdNumber,
        savingTrialGroupContract,
        saveTrialGroupContractSuccess,
        loadTrialGroupContract,
        trialGroupContractIdNumber,
        navigate,
        saveAndReturn,
        clearTrialGroupContract,
    ]);

    // Failed saving
    useEffect(() => {
        if (!savingTrialGroupContract && saveTrialGroupContractFailure) {
            Alert.error(
                <NotificationAlert
                    alertContent={renderRegistrationFormSaveBlock()}
                    icon={FontAwesomeIcons.Solid.BAN}
                />
            );
        }
    }, [
        savingTrialGroupContract,
        saveTrialGroupContractSuccess,
        loadTrialGroupContract,
        trialGroupContractIdNumber,
    ]);

    // Cleanup effect to clear the trial group & contract on component unmount
    useEffect(() => {
        return () => {
            clearTrialGroupContract();
            clearTrialGroup();
            resetForm(reduxFormName);
        };
    }, [clearTrialGroupContract, clearTrialGroup, resetForm, reduxFormName]);

    const handleOnSubmit = async (data: any) => {
        if (!hasBeenSubmitted) {
            setHasBeenSubmitted(true);
        }

        if (data) {
            let changes: Dtos.TrialGroupContract = {} as Dtos.TrialGroupContract;

            if (!data.trialGroupId) {
                changes.trialGroupId = trialGroupIdNumber;
            }

            await saveTrialGroupContract(Object.assign({ ...data }, changes));

            if (data.saveAndReturn) {
                setSaveAndReturn(true);
            }
        }
    };

    const handleOnSubmitFailed = (_data: Dtos.TrialGroupContract) => {
        Alert.error(
            <NotificationAlert
                alertContent={renderRegistrationFormSaveBlock()}
                icon={FontAwesomeIcons.Solid.BAN}
            />
        );
    };

    const onCancel = () => {
        const baseRoute = location?.pathname?.split("/")[1];

        const url =
            baseRoute === "trials"
                ? `/trials/${trialIdNumber}/groups/${groupIdNumber}/trialgroup/${trialGroupIdNumber}/contracts`
                : `/groups/${groupIdNumber}/trials/${trialIdNumber}/trialgroup/${trialGroupIdNumber}/contracts`;
        navigate(url);
    };

    // Debounce validation after the initial submit to clear form validation errors as the form get updated.
    useEffect(() => {
        const debounceValidate = debounce(async () => {
            let changes: Dtos.TrialGroupContract = {} as Dtos.TrialGroupContract;

            if (form && hasBeenSubmitted && !validatingTrialGroupContract) {
                await validateTrialGroupContract(
                    Object.assign({ ...form }, changes)
                );
            }
        }, 250);

        debounceValidate();

        return () => {
            debounceValidate.cancel();
        };
    }, [form]);

    const openLink = (link: string) => {
        window.open(`${link}`, "_blank");
        return null;
    };

    const renderContent = useMemo(() => {
        const hasPermission: boolean =
            AuthenticationHelper.hasCreateEditPermissionFor(
                undefined,
                trialGroupContractIdNumber,
                permissions,
                Dtos.Permission.TrialGroupContractUpdate,
                Dtos.Permission.TrialGroupContractCreate
            );
        const formDisabled: boolean =
            !hasPermission ||
            savingTrialGroupContract ||
            (formState && formState.formStatus == Dtos.FormStatus.Disabled);

        let formComponents: { [index: string]: RegistrationFormComponent } = {
            detailsGroup: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <div className="card card-shadow" style={{ zIndex: 1 }}>
                            <div className="form-row pt-4 pb-2 px-4">
                                <div className="col-12">
                                    {renderFormControls(
                                        form,
                                        "trialGroupContract",
                                        {
                                            name: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-form-label col-sm-12 col-xl-2",
                                                fieldClassName: "col-sm-12 col-xl-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row py-2 px-4">
                                <div className="col-12">
                                    {renderFormControls(
                                        form,
                                        "trialGroupContract",
                                        {
                                            location: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    addOnRightButton: (
                                                        <button
                                                            className="btn btn-secondary"
                                                            type="button"
                                                            id="button-addon2"
                                                            disabled={form?.location == null || !isUrl(form?.location)}
                                                            style={{
                                                                cursor: form?.location == null || !isUrl(form?.location)
                                                                        ? "not-allowed"
                                                                        : "pointer",
                                                                padding: "4px 10px",
                                                            }}
                                                            onClick={() => {
                                                                openLink(form.location);
                                                            }}
                                                        >
                                                            <FontAwesomeIcon icon={FontAwesomeIcons.Duotone.ARROWUPRIGHTFROMSQUARE} />
                                                        </button>
                                                    ),
                                                },
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-form-label col-sm-12 col-xl-2",
                                                fieldClassName: "col-sm-12 col-xl-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row pt-2 pb-4 px-4">
                                <div className="col-6">
                                    {renderFormControls(
                                        form,
                                        "trialGroupContract",
                                        {
                                            dateSentToSite: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: DatePickerFormInputFabric,
                                                inputDisabled: formDisabled,
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-form-label col-sm-12 col-xl-4",
                                                fieldClassName: "col-sm-12 col-xl-8 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-6">
                                    {renderFormControls(
                                        form,
                                        "trialGroupContract",
                                        {
                                            dateConfirmedBySite: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: DatePickerFormInputFabric,
                                                inputDisabled: formDisabled,
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-form-label col-sm-12 col-xl-4",
                                                fieldClassName: "col-sm-12 col-xl-8 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ),
            },
        };

        return (
            <>
                <div className="row">
                    <div className={"col-auto mb-2"}>
                        <h1>Details</h1>
                    </div>
                    <div
                        className="col d-flex justify-content-end align-items-stretch"
                        style={{ height: "38px" }}
                    >
                        {/* {(AuthenticationHelper.hasPermission(permissions, Dtos.Permission.LabView) && trialGroupContractIdNumber) ? (
                            <Link 
                                className={"btn btn-primary"} 
                                url={`/labs/${trialGroupContract?.laboratoryId}/details`}
                                target='_blank'
                            >
                                {`Primary  `}
                                <FontAwesomeIcon icon={FontAwesomeIcons.Duotone.ARROWUPRIGHTFROMSQUARE} />
                            </Link>
                        ) : null} */}
                    </div>
                </div>
                <Form
                    model={reduxFormName}
                    onSubmit={(val, _event) => handleOnSubmit(val)}
                    onSubmitFailed={(val) => handleOnSubmitFailed(val)}
                >
                    {renderFormControls(
                        form,
                        "trialGroupContract",
                        formComponents,
                        lookups,
                        validationFailures,
                        formProperties,
                        changeForm
                    )}
                    <div className="mt-4 mb-4 text-right">
                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(reduxFormName + ".saveAndReturn", false);
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit
                        </button>
                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(reduxFormName + ".saveAndReturn", true);
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit And Return
                        </button>
                        <button
                            onClick={onCancel}
                            className="btn btn-primary mr-2"
                        >
                            Cancel
                        </button>
                    </div>
                </Form>
            </>
        );
    }, [
        AuthenticationHelper,
        trialGroupContractIdNumber,
        permissions,
        savingTrialGroupContract,
        formState,
        handleOnSubmit,
        handleOnSubmitFailed,
        renderFormControls,
        lookups,
        validationFailures,
        formProperties,
        form,
        changeForm,
        reduxFormName,
        onCancel,
        trialGroup,
        trialGroupContract,
    ]);

    return (
        <RestrictedLayout
            permission={Dtos.Permission.TrialGroupContractView}
            loading={
                loadingTrialGroupContract ||
                creatingTrialGroupContract ||
                savingTrialGroupContract
            }
        >
            {renderContent}
        </RestrictedLayout>
    );
};

const mapStateToProps = (
    state: ITmdState
): Partial<TrialGroupContractPageProps> => {
    let trialGroupContract: Dtos.TrialGroupContract | undefined = !(
        state.trialGroupContract.formData instanceof Array
    )
        ? state.trialGroupContract.formData
        : undefined;
    let trialGroup: Dtos.TrialGroup | undefined = !(
        state.trialGroup.formData instanceof Array
    )
        ? state.trialGroup.formData
        : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        trialGroupContract: trialGroupContract!,
        loadingTrialGroupContract: state.trialGroupContract.loadState && state.trialGroupContract.loadState.status == RequestState.Pending,
        loadTrialGroupContractSuccess: state.trialGroupContract.loadState && state.trialGroupContract.loadState.status == RequestState.Success,
        loadTrialGroupContractFailure: state.trialGroupContract.loadState && state.trialGroupContract.loadState.status == RequestState.Failure,
        loadTrialGroupContractErrorCode: state.trialGroupContract.loadState.errorCode,
        savingTrialGroupContract: state.trialGroupContract.saveState && state.trialGroupContract.saveState.status == RequestState.Pending,
        saveTrialGroupContractSuccess: state.trialGroupContract.saveState && state.trialGroupContract.saveState.status == RequestState.Success,
        saveTrialGroupContractFailure: state.trialGroupContract.saveState && state.trialGroupContract.saveState.status == RequestState.Failure,
        creatingTrialGroupContract: state.trialGroupContract.createState && state.trialGroupContract.createState.status == RequestState.Pending,
        createTrialGroupContractSuccess: state.trialGroupContract.createState && state.trialGroupContract.createState.status == RequestState.Success,
        createTrialGroupContractFailure: state.trialGroupContract.createState && state.trialGroupContract.createState.status == RequestState.Failure,
        validatingTrialGroupContract: state.trialGroupContract.validateState && state.trialGroupContract.validateState.status == RequestState.Pending,
        user: state.user.data,
        permissions: permissions,
        lookups: state.trialGroupContract.lookups,
        form: state.reduxForms.trialGroupContract,
        formState: state.trialGroupContract.formState,
        formProperties: state.trialGroupContract.formProperties,
        reduxFormState: state.reduxForms.formState.trialGroupContract,
        validationFailures: trialGroupContract
            ? state.trialGroupContract.validationFailures
            : undefined,
        trialGroup: trialGroup!,
        loadingTrialGroup: state.trialGroup.loadState && state.trialGroup.loadState.status == RequestState.Pending,
        loadTrialGroupSuccess: state.trialGroup.loadState && state.trialGroup.loadState.status == RequestState.Success,
        loadTrialGroupFailure: state.trialGroup.loadState && state.trialGroup.loadState.status == RequestState.Failure,
        loadTrialGroupErrorCode: state.trialGroup.loadState.errorCode,
    };
};

const mapDispatchToProps = (dispatch): Partial<TrialGroupContractPageProps> => {
    return {
        loadForm: bindActionCreators(actions.load, dispatch),
        changeForm: bindActionCreators(actions.change, dispatch),
        resetForm: bindActionCreators(actions.reset, dispatch),
        loadTrialGroupContract: bindActionCreators(TrialGroupContractActions.LoadTrialGroupContractById, dispatch),
        clearTrialGroupContract: bindActionCreators(TrialGroupContractActions.Clear, dispatch),
        saveTrialGroupContract: bindActionCreators(TrialGroupContractActions.SaveTrialGroupContract, dispatch),
        createTrialGroupContract: bindActionCreators(TrialGroupContractActions.CreateTrialGroupContract, dispatch),
        validateTrialGroupContract: bindActionCreators(TrialGroupContractActions.ValidateTrialGroupContract, dispatch),
        setBreadcrumbs: bindActionCreators(NavigationActions.SetBreadcrumbs, dispatch),
        setDrawerItems: bindActionCreators(NavigationActions.SetDrawerItems, dispatch),
        loadTrialGroup: bindActionCreators(TrialGroupActions.LoadTrialGroupById, dispatch),
        clearTrialGroup: bindActionCreators(TrialGroupActions.Clear, dispatch)
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(TrialGroupContractPage);
